import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { RedoOutlined, PlusOutlined, CloseOutlined, ExportOutlined } from '@ant-design/icons';
import { Drawer, Select, Input, Tooltip, Switch, Table, Modal, message, DatePicker } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import { ButtonWrapper } from 'components';
import { Box, Flex, Text } from 'atoms';
import { FilterTitle } from 'components/common';
import FilterTag from 'components/common/FilterTag';
import { FilterAtnButton } from 'utils/commonStyled';
import { NodeTableConfigV2 } from 'utils/table-config/NodeTableConfigV2';
import { searchNodes, removeNode, updateNode, exportNodeCSVV2 } from 'store/nodeSliceV2';
import { getProjectListV2 } from 'store/projectSliceV2';
import { usePersistedState } from 'hooks';

const { Search } = Input;
const { RangePicker } = DatePicker;

const LtSearchBar = styled(Search)`
	border: none;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

const StyledTable = styled(Table)`
	.ant-table-thead > tr > th {
		background: #18405d;
		color: #fff;
		font-weight: 600;
	}
	.ant-table-tbody > tr > td {
		border-bottom: 1px solid #f0f0f0;
	}
	.ant-table-tbody > tr:hover > td {
		background: #f5f5f5;
	}
`;

// Node type constants
const NODE_TYPE_WAREHOUSE = 'Warehouse';
const NODE_TYPE_STORE = 'Store';
const NODE_TYPE_HUB = 'Hub';
const NODE_TYPE_COLD_STORAGE = 'Cold Storage';
const NODE_TYPE_FULFILLMENT_CENTER = 'Fulfillment Center';

const allOption = {
	value: '',
	label: 'All',
};

const NodeTypeOptions = [
	{ value: NODE_TYPE_WAREHOUSE, label: NODE_TYPE_WAREHOUSE },
	{ value: NODE_TYPE_STORE, label: NODE_TYPE_STORE },
	{ value: NODE_TYPE_HUB, label: NODE_TYPE_HUB },
	{ value: NODE_TYPE_COLD_STORAGE, label: NODE_TYPE_COLD_STORAGE },
	{ value: NODE_TYPE_FULFILLMENT_CENTER, label: NODE_TYPE_FULFILLMENT_CENTER },
];
NodeTypeOptions.unshift(allOption);

const StatusOptions = [
	{ value: 'true', label: 'Active' },
	{ value: 'false', label: 'Inactive' },
];
StatusOptions.unshift(allOption);

export default function NodeListPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const [searchText, setSearchText] = usePersistedState('nodeList:search', '');
	const [filters, setFilters] = usePersistedState('nodeList:filters', {
		node_type: '',
		city: '',
		is_active: '',
		created_from: '',
		created_to: '',
	});
	const [pagination, setPagination] = usePersistedState('nodeList:pagination', {
		current: 1,
		pageSize: 10,
	});
	const [nodesData, setNodesData] = useState([]);
	const [totalNodes, setTotalNodes] = useState(0);
	const [loading, setLoading] = useState(false);
	const [removeModalVisible, setRemoveModalVisible] = useState(false);
	const [selectedNodeForRemoval, setSelectedNodeForRemoval] = useState(null);
	const [removalReason, setRemovalReason] = useState('');
	const [cityOptions, setCityOptions] = useState([allOption]);
	const [exporting, setExporting] = useState(false);
	const [dateRange, setDateRange] = useState(null);
	const [projects, setProjects] = useState([]);
	const [selectedProject, setSelectedProject] = usePersistedState('nodeList:project', undefined);
	const [loadingProjects, setLoadingProjects] = useState(false);

	// Fetch projects for filter dropdown
	const fetchProjects = async () => {
		setLoadingProjects(true);
		try {
			const result = await dispatch(
				getProjectListV2({
					filter_by: {},
					pagination: { page: 1, per_page: 100 },
				})
			);
			if (result?.rows) {
				setProjects(
					result.rows.map((p) => ({
						value: p.id,
						label: p.project_code || p.project_name,
					}))
				);
			}
		} catch (error) {
			console.error('Error fetching projects:', error);
		} finally {
			setLoadingProjects(false);
		}
	};

	// Fetch projects when filter drawer opens
	useEffect(() => {
		if (open && projects.length === 0) {
			fetchProjects();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Fetch nodes from API
	const fetchNodes = async () => {
		setLoading(true);
		try {
			const filter_by = {};

			// Add node_name search
			if (searchText) {
				filter_by.node_name = searchText;
			}

			// Add node type filter
			if (filters.node_type) {
				filter_by.node_type = filters.node_type;
			}

			// Add city filter
			if (filters.city) {
				filter_by.city = filters.city;
			}

			// Add status filter
			if (
				filters.is_active !== undefined &&
				filters.is_active !== null &&
				filters.is_active !== ''
			) {
				filter_by.is_active = filters.is_active === 'true';
			}

			// Add project filter
			if (selectedProject) {
				filter_by.project_id = selectedProject;
			}

			if (filters.created_from) {
				filter_by.created_from = filters.created_from;
			}

			if (filters.created_to) {
				filter_by.created_to = filters.created_to;
			}

			const response = await dispatch(
				searchNodes({
					filter_by,
					pagination: {
						page: pagination.current,
						per_page: pagination.pageSize,
					},
					sort_by: {
						field: 'created_at',
						order: 'desc',
					},
				})
			);

			if (response?.data?.nodes) {
				setNodesData(response.data.nodes);
				setTotalNodes(response.data.pagination?.total || response.data.nodes.length);
			}
		} catch (error) {
			message.error('Failed to fetch nodes');
			console.error('Fetch nodes error:', error);
		} finally {
			setLoading(false);
		}
	};

	// Fetch nodes on mount and when filters/pagination change
	useEffect(() => {
		fetchNodes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.current, pagination.pageSize, filters, searchText, selectedProject]);

	// Fetch all nodes across all pages to extract unique cities directly
	useEffect(() => {
		const fetchAllCitiesFromNodes = async () => {
			try {
				const firstRes = await dispatch(
					searchNodes({ filter_by: {}, pagination: { page: 1, per_page: 50 } })
				);

				const initialNodes = firstRes?.data?.nodes || [];
				if (!initialNodes.length) {
					setCityOptions([allOption]);
					return;
				}

				const total = firstRes.data.pagination?.total || 0;
				const totalPages = Math.ceil(total / 50);

				const promises = Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) =>
					dispatch(
						searchNodes({ filter_by: {}, pagination: { page: i + 2, per_page: 50 } })
					)
				);

				const results = await Promise.all(promises);
				const extraNodes = results.flatMap((res) => res?.data?.nodes || []);
				const allNodes = [...initialNodes, ...extraNodes];

				const uniqueCities = [
					...new Set(allNodes.map((node) => node?.city).filter(Boolean)),
				].sort((a, b) => a.localeCompare(b));

				setCityOptions([
					allOption,
					...uniqueCities.map((city) => ({ value: city, label: city })),
				]);
			} catch (error) {
				console.error('Failed to load cities across all nodes:', error);
				setCityOptions([allOption]);
			}
		};

		fetchAllCitiesFromNodes();
	}, [dispatch]);

	// Handle status toggle
	const handleStatusToggle = async (record) => {
		try {
			await dispatch(
				updateNode({
					req_data: {
						id: record.id,
						is_active: !record.is_active,
					},
				})
			);
			// Refresh nodes list
			fetchNodes();
		} catch (error) {
			message.error('Failed to update node status');
			console.error('Status toggle error:', error);
		}
	};

	// Handle node removal
	const handleRemoveNode = async () => {
		if (!removalReason.trim()) {
			message.warning('Please provide a removal reason');
			return;
		}

		try {
			await dispatch(
				removeNode({
					node_id: selectedNodeForRemoval.id,
					removal_reason: removalReason,
				})
			);
			// Refresh nodes list
			fetchNodes();
			// Close modal and reset
			setRemoveModalVisible(false);
			setSelectedNodeForRemoval(null);
			setRemovalReason('');
		} catch (error) {
			const errorCode = error?.response?.data?.error?.code;
			const errorMessage = error?.response?.data?.error?.message;

			if (errorCode === 'INVALID_STATE') {
				const assignedProjects =
					error?.response?.data?.error?.details?.assigned_projects || [];
				const projectList = assignedProjects.map((p) => p.project_code).join(', ');
				message.error(
					`Cannot remove node. It is assigned to projects: ${projectList}. Remove from projects first.`
				);
			} else {
				message.error(errorMessage || 'Failed to remove node');
			}
			console.error('Remove node error:', error);
		}
	};

	// Cancel remove modal
	const handleCancelRemove = () => {
		setRemoveModalVisible(false);
		setSelectedNodeForRemoval(null);
		setRemovalReason('');
	};

	// Use table config and map columns to include status toggle handler
	const columns = NodeTableConfigV2.map((col) => {
		if (col.key === 'is_active') {
			return {
				...col,
				render: (isActive, record) => (
					<Switch
						checked={isActive}
						onChange={() => handleStatusToggle(record)}
						checkedChildren="Active"
						unCheckedChildren="Inactive"
						style={{
							backgroundColor: isActive ? '#1990FF' : '#C6C6C8',
						}}
					/>
				),
			};
		}
		return col;
	});

	// Get filter tags for display
	const hasNodeTypeFilter = () =>
		filters.node_type !== undefined && filters.node_type !== null && filters.node_type !== '';
	const hasCityFilter = () =>
		filters.city !== undefined && filters.city !== null && filters.city !== '';
	const hasStatusFilter = () =>
		filters.is_active !== undefined && filters.is_active !== null && filters.is_active !== '';
	const hasProjectFilter = () =>
		selectedProject !== undefined && selectedProject !== null && selectedProject !== '';
	const hasDateRangeFilter = () => filters.created_from && filters.created_to;

	const activeFiltersCount = [
		hasNodeTypeFilter(),
		hasCityFilter(),
		hasStatusFilter(),
		hasProjectFilter(),
		hasDateRangeFilter(),
	].filter(Boolean).length;

	const resetToFirstPage = () => {
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const updateFilters = (updater) => {
		setFilters((prev) =>
			typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }
		);
		resetToFirstPage();
	};

	const handleRemoveNodeTypeFilter = () => {
		updateFilters({ node_type: '' });
	};
	const handleRemoveCityFilter = () => {
		updateFilters({ city: '' });
	};
	const handleRemoveStatusFilter = () => {
		updateFilters({ is_active: '' });
	};
	const handleRemoveProjectFilter = () => {
		setSelectedProject(undefined);
		resetToFirstPage();
	};
	const handleRemoveDateRangeFilter = () => {
		setDateRange(null);
		updateFilters({ created_from: '', created_to: '' });
	};

	const handleSearch = (value) => {
		setSearchText(value ? value.trim() : '');
		resetToFirstPage();
	};

	const handleSync = () => {
		setSearchText('');
		setFilters({
			node_type: '',
			city: '',
			is_active: '',
			created_from: '',
			created_to: '',
		});
		setDateRange(null);
		setSelectedProject(undefined);
		setPagination({ current: 1, pageSize: 10 });
		fetchNodes();
	};

	const handleExport = async () => {
		setExporting(true);
		try {
			const filter_by = {};

			// Add node_name search
			if (searchText) {
				filter_by.node_name = searchText;
			}

			// Add node type filter
			if (filters.node_type) {
				filter_by.node_type = filters.node_type;
			}

			// Add city filter
			if (filters.city) {
				filter_by.city = filters.city;
			}

			// Add status filter
			if (
				filters.is_active !== undefined &&
				filters.is_active !== null &&
				filters.is_active !== ''
			) {
				filter_by.is_active = filters.is_active === 'true';
			}

			// Add project filter
			if (selectedProject) {
				filter_by.project_id = selectedProject;
			}

			if (filters.created_from) {
				filter_by.created_from = filters.created_from;
			}

			if (filters.created_to) {
				filter_by.created_to = filters.created_to;
			}

			await dispatch(
				exportNodeCSVV2({
					filter_by,
				})
			);
		} catch (error) {
			message.error('Failed to export nodes. Please try again.');
			console.error('Export error:', error);
		} finally {
			setExporting(false);
		}
	};

	const handleCreate = () => {
		navigate('/nodes/create');
	};

	const handleResetFilters = () => {
		setFilters({
			node_type: '',
			city: '',
			is_active: '',
			created_from: '',
			created_to: '',
		});
		setDateRange(null);
		setSelectedProject(undefined);
		resetToFirstPage();
		setOpen(false);
	};

	const handleTableChange = (paginationConfig) => {
		setPagination({
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
		});
	};

	return (
		<>
			<Flex justifyContent="space-between" mt={3}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							enterButton="Search"
							placeholder="Search"
							allowClear
							onSearch={handleSearch}
						/>
					</Box>
					<FilterAtnButton onClick={() => setOpen(true)}>FILTERS</FilterAtnButton>
					<FilterDisplayBox>
						{hasNodeTypeFilter() && (
							<FilterTag onClose={handleRemoveNodeTypeFilter}>Node Type</FilterTag>
						)}
						{hasCityFilter() && (
							<FilterTag onClose={handleRemoveCityFilter}>City</FilterTag>
						)}
						{hasStatusFilter() && (
							<FilterTag onClose={handleRemoveStatusFilter}>Status</FilterTag>
						)}
						{hasProjectFilter() && (
							<FilterTag onClose={handleRemoveProjectFilter}>Project</FilterTag>
						)}
						{hasDateRangeFilter() && (
							<FilterTag onClose={handleRemoveDateRangeFilter}>Date Range</FilterTag>
						)}
						{activeFiltersCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={handleResetFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex>
					<ButtonWrapper
						onClick={handleExport}
						icons={<ExportOutlined />}
						loading={exporting}
						disabled={exporting}
					>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />}>
						SYNC
					</ButtonWrapper>
					<ButtonWrapper onClick={handleCreate} icons={<PlusOutlined />}>
						CREATE
					</ButtonWrapper>
				</Flex>
			</Flex>
			<Box mt={3}>
				<StyledTable
					rowKey="id"
					columns={columns}
					dataSource={nodesData}
					loading={loading}
					scroll={{ x: 'max-content' }}
					pagination={{
						...pagination,
						total: totalNodes,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
						pageSizeOptions: ['10', '20', '50', '100'],
					}}
					onChange={handleTableChange}
				/>
			</Box>

			<Drawer
				className="userList-drawer"
				title={<FilterTitle title="Filters" onClose={() => setOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setOpen(false)}
				open={open}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px">Node Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="node_type"
							value={filters.node_type}
							onChange={(val) => updateFilters({ node_type: val })}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select Node Type"
							options={NodeTypeOptions}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">City</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="city"
							value={filters.city}
							onChange={(val) => updateFilters({ city: val })}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select City"
							options={cityOptions}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="is_active"
							value={filters.is_active}
							onChange={(val) => updateFilters({ is_active: val })}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select Status"
							options={StatusOptions}
						/>
					</Box>

					{/* Project Filter */}
					<Box mb="2rem">
						<Text mb="5px">Project</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							value={selectedProject}
							onChange={(val) => {
								setSelectedProject(val);
								resetToFirstPage();
							}}
							allowClear
							loading={loadingProjects}
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
							}
							placeholder="Select Project"
							options={projects}
						/>
					</Box>

					{/* Date Range Filter */}
					<Box mb="2rem">
						<Text mb="5px">Created At</Text>
						<RangePicker
							style={{ width: '100%' }}
							placeholder={['Start Date', 'End Date']}
							value={dateRange}
							format="DD/MM/YYYY"
							onChange={(dates) => {
								setDateRange(dates);
								if (dates && dates[0] && dates[1]) {
									updateFilters((prev) => ({
										...prev,
										created_from: moment(dates[0]).startOf('day').toISOString(),
										created_to: moment(dates[1]).endOf('day').toISOString(),
									}));
								} else {
									updateFilters({ created_from: '', created_to: '' });
								}
							}}
						/>
					</Box>

					<Box>
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={handleResetFilters}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>

			{/* Remove Node Confirmation Modal */}
			<Modal
				title="Remove Node"
				open={removeModalVisible}
				onOk={handleRemoveNode}
				onCancel={handleCancelRemove}
				okText="Remove"
				okButtonProps={{ danger: true }}
				cancelText="Cancel"
			>
				<Box>
					<Text mb={2}>
						Are you sure you want to remove node:{' '}
						<strong>{selectedNodeForRemoval?.node_name}</strong>?
					</Text>
					<Box mt={3}>
						<Text mb={1}>Removal Reason (Required):</Text>
						<Input.TextArea
							rows={4}
							placeholder="Enter reason for removing this node..."
							value={removalReason}
							onChange={(e) => setRemovalReason(e.target.value)}
							maxLength={500}
							showCount
						/>
					</Box>
					<Box mt={2}>
						<Text fontSize="12px" color="#666">
							Note: Node cannot be removed if it is assigned to active projects.
						</Text>
					</Box>
				</Box>
			</Modal>
		</>
	);
}
